package com.rosario.juridico.ui.screens

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import com.rosario.juridico.ui.components.WebScrollableBox
import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Calculate
import androidx.compose.material.icons.filled.DeleteSweep
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.*
import androidx.compose.material3.MaterialTheme.colorScheme
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.*
import com.rosario.juridico.navigation.Routes
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay

// ─────────────────────────────────────────────────────────────────────────────
// TABLA ART. 1746 CCC — Vigente desde el 01/08/2026
// ─────────────────────────────────────────────────────────────────────────────

private val TABLA_1746: Map<Int, Double> = mapOf(
    18 to 1_218_323.63, 19 to 1_216_769.64, 20 to 1_215_217.58,
    21 to 1_213_667.59, 22 to 1_212_119.54, 23 to 1_210_572.63,
    24 to 1_208_647.50, 25 to 1_206_703.61, 26 to 1_204_603.50,
    27 to 1_202_324.22, 28 to 1_199_878.00, 29 to 1_197_224.69,
    30 to 1_194_364.73, 31 to 1_191_275.97, 32 to 1_187_932.92,
    33 to 1_184_332.86, 34 to 1_180_436.83, 35 to 1_176_233.67,
    36 to 1_171_696.05, 37 to 1_166_746.45, 38 to 1_161_422.85,
    39 to 1_155_777.08, 40 to 1_149_601.03, 41 to 1_142_923.81,
    42 to 1_135_723.53, 43 to 1_127_940.71, 44 to 1_119_536.55,
    45 to 1_110_461.38, 46 to 1_100_677.35, 47 to 1_090_640.66,
    48 to 1_078_290.60, 49 to 1_065_290.60, 50 to 1_052_951.84,
    51 to 1_038_569.55, 52 to 1_022_997.67, 53 to 1_005_190.34,
    54 to 988_049.75,   55 to 968_291.86,   56 to 947_443.25,
    57 to 924_438.25,   58 to 899_746.05,   59 to 873_096.67,
    60 to 844_302.67,   61 to 813_202.10,   62 to 779_625.41,
    63 to 743_365.45,   64 to 704_186.36,   65 to 661_879.35
)

private const val INGRESO_REF = 966_075.02

private fun formatoPesos(valor: Double): String {
    return "$ ${valor.toFormato(2, true)}"
}

// ─────────────────────────────────────────────────────────────────────────────
// MODELO
// ─────────────────────────────────────────────────────────────────────────────

data class ResultadoIncapacidad(
    val edadVictima:          Int,
    val tipoIncapacidad:      String,
    val porcentaje:           Double,
    val ingresoUsado:         Double,
    val factorTabla:          Double?,
    val incapacidadBase:      Double,
    val danoMoral:            Double,
    val lucroSesante:         Double,
    val danoExtrapatrimonial: Double,
    val privacionRodado:      Double,
    val totalGeneral:         Double,
    val detalleEdad:          String
)

// ─────────────────────────────────────────────────────────────────────────────
// PANTALLA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun PantallaCalculadoraIncapacidad(navController: NavController) {
    Column(
        modifier = Modifier
            .fillMaxSize()
    ) {
        UsageCounterIndicator()
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            ContenidoCalculadoraIncapacidad(navController = navController)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// FORMULARIO
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun ContenidoCalculadoraIncapacidad(navController: NavController) {
    val scrollState = rememberScrollState()
    var resultadoVisibleConDelay by remember { mutableStateOf(false) }

    var edad                        by remember { mutableStateOf("") }
    var porcentajeIncapacidad       by remember { mutableStateOf("") }
    var ingresoMensual              by remember { mutableStateOf("") }
    var usarIngresoReferencia       by remember { mutableStateOf(false) }
    var tipoIncapacidad             by remember { mutableStateOf("Parcial Permanente") }
    var incluyeDanoMoral            by remember { mutableStateOf(false) }
    var porcentajeDanoMoral         by remember { mutableStateOf("") }
    var incluyeLucroSesante         by remember { mutableStateOf(false) }
    var mesesLucroSesante           by remember { mutableStateOf("") }
    var incluyeDanoExtrapatrimonial by remember { mutableStateOf(false) }
    var tipoExtrapatrimonial        by remember { mutableStateOf("muerte de hijo") }
    var incluyePrivacionRodado      by remember { mutableStateOf(false) }
    var diasPrivacion               by remember { mutableStateOf("") }
    var tipoRodado                  by remember { mutableStateOf("general") }
    var resultado                   by remember { mutableStateOf<ResultadoIncapacidad?>(null) }
    var errorMsg                    by remember { mutableStateOf("") }

    val typesIncapacidad      = listOf("Parcial Permanente", "Total Permanente", "Parcial Transitoria", "Total Transitoria")
    val typesExtrapatrimonial = listOf("muerte de hijo", "muerte de progenitor", "muerte de cónyuge/unión convivencial", "muerte de otros convivientes")

    val thousandSeparator = ThousandSeparatorVisualTransformation()

    fun limpiar() {
        edad = ""; porcentajeIncapacidad = ""; ingresoMensual = ""
        usarIngresoReferencia = false; tipoIncapacidad = "Parcial Permanente"
        incluyeDanoMoral = false; porcentajeDanoMoral = ""
        incluyeLucroSesante = false; mesesLucroSesante = ""
        incluyeDanoExtrapatrimonial = false; tipoExtrapatrimonial = "muerte de hijo"
        incluyePrivacionRodado = false; diasPrivacion = ""; tipoRodado = "general"
        resultado = null; errorMsg = ""; resultadoVisibleConDelay = false
    }

    // --- LÓGICA DE AUTO-SCROLL (FASE 4.1 QA) ---
    LaunchedEffect(incluyeDanoMoral) { if (incluyeDanoMoral) { delay(3000); scrollState.animateScrollTo(scrollState.value + 150) } }
    LaunchedEffect(incluyeLucroSesante) { if (incluyeLucroSesante) { delay(3000); scrollState.animateScrollTo(scrollState.value + 150) } }
    LaunchedEffect(incluyeDanoExtrapatrimonial) { if (incluyeDanoExtrapatrimonial) { delay(3000); scrollState.animateScrollTo(scrollState.value + 200) } }
    LaunchedEffect(incluyePrivacionRodado) { if (incluyePrivacionRodado) { delay(3000); scrollState.animateScrollTo(scrollState.value + 200) } }

    WebScrollableBox(scrollState = scrollState, modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier.fillMaxSize().imePadding().verticalScroll(scrollState),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
        // ── Datos de la víctima ───────────────────────────────────────────────
        SeccionTituloInc("Datos de la Víctima")
        OutlinedTextField(
            value = edad,
            onValueChange = { if (it.length <= 3) edad = it.filter { c -> c.isDigit() } },
            label = { Text("Edad al momento del hecho") },
            suffix = { Text("años") },
            modifier = Modifier.fillMaxWidth().resaltadoGuia(edad.isEmpty()),
            singleLine = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
        )

        Text("Tipo de incapacidad", style = MaterialTheme.typography.labelLarge)
        Column {
            typesIncapacidad.forEach { tipo ->
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                    RadioButton(selected = tipoIncapacidad == tipo, onClick = { tipoIncapacidad = tipo })
                    Text(tipo, modifier = Modifier.padding(start = 4.dp))
                }
            }
        }

        OutlinedTextField(
            value = porcentajeIncapacidad,
            onValueChange = { porcentajeIncapacidad = it.filter { c -> c.isDigit() || c == '.' }.take(5) },
            label = { Text("Porcentaje de incapacidad") },
            suffix = { Text("%") },
            modifier = Modifier.fillMaxWidth().resaltadoGuia(porcentajeIncapacidad.isEmpty()),
            singleLine = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
        )

        // ── Ingreso mensual ───────────────────────────────────────────────────
        SeccionTituloInc("Ingreso Mensual")
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
            Checkbox(checked = usarIngresoReferencia, onCheckedChange = { usarIngresoReferencia = it })
            Column(modifier = Modifier.padding(start = 8.dp)) {
                Text("Usar ingreso de referencia oficial", fontWeight = FontWeight.Medium)
                Text("${formatoPesos(INGRESO_REF)} (vigente desde el 01/08/2026)", style = MaterialTheme.typography.bodySmall, color = colorScheme.onSurfaceVariant)
            }
        }
        AnimatedVisibility(visible = !usarIngresoReferencia) {
            OutlinedTextField(
                value = ingresoMensual,
                onValueChange = { ingresoMensual = it.filter { c -> c.isDigit() } },
                label = { Text("Ingreso mensual real neto") },
                prefix = { Text("$ ") },
                modifier = Modifier.fillMaxWidth().resaltadoGuia(ingresoMensual.isEmpty()),
                singleLine = true,
                supportingText = { Text("Remuneración neta mensual de la víctima") },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                visualTransformation = thousandSeparator
            )
        }

        // ── Rubros adicionales ────────────────────────────────────────────────
        SeccionTituloInc("Rubros Adicionales")

        CheckboxConTituloInc(incluyeDanoMoral, { incluyeDanoMoral = it }, "Incluir Daño Moral", "% sobre el daño patrimonial (criterio judicial: 30%–80%)")
        AnimatedVisibility(visible = incluyeDanoMoral) {
            OutlinedTextField(
                value = porcentajeDanoMoral,
                onValueChange = { porcentajeDanoMoral = it.filter { c -> c.isDigit() || c == '.' }.take(5) },
                label = { Text("% Daño Moral sobre incapacidad") },
                suffix = { Text("%") },
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp).resaltadoGuia(porcentajeDanoMoral.isEmpty()),
                singleLine = true,
                placeholder = { Text("Ej: 50") },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
            )
        }

        CheckboxConTituloInc(incluyeLucroSesante, { incluyeLucroSesante = it }, "Incluir Lucro Cesante / Inc. Transitoria", "Ingresos perdidos durante el período de recuperación")
        AnimatedVisibility(visible = incluyeLucroSesante) {
            OutlinedTextField(
                value = mesesLucroSesante,
                onValueChange = { mesesLucroSesante = it.filter { c -> c.isDigit() }.take(3) },
                label = { Text("Meses de incapacidad transitoria") },
                suffix = { Text("meses") },
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp).resaltadoGuia(mesesLucroSesante.isEmpty()),
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
            )
        }

        CheckboxConTituloInc(incluyeDanoExtrapatrimonial, { incluyeDanoExtrapatrimonial = it }, "Incluir Daño Extrapatrimonial por Muerte", "Tabla orientativa complementaria art. 1746 CCC")
        AnimatedVisibility(visible = incluyeDanoExtrapatrimonial) {
            Column(modifier = Modifier.padding(top = 8.dp)) {
                Text("Tipo de vínculo:", style = MaterialTheme.typography.labelMedium)
                typesExtrapatrimonial.forEach { tipo ->
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        RadioButton(selected = tipoExtrapatrimonial == tipo, onClick = { tipoExtrapatrimonial = tipo })
                        val montoRef = when (tipo) {
                            "muerte de hijo"                       -> "hasta $ 32.000.000"
                            "muerte de progenitor"                 -> "hasta $ 27.000.000"
                            "muerte de cónyuge/unión convivencial" -> "hasta $ 29.000.000"
                            else                                   -> "hasta $ 8.000.000"
                        }
                        Column(modifier = Modifier.padding(start = 4.dp)) {
                            Text(tipo.replaceFirstChar { it.uppercase() }, fontWeight = FontWeight.Medium)
                            Text(montoRef, style = MaterialTheme.typography.bodySmall, color = colorScheme.onSurfaceVariant)
                        }
                    }
                }
            }
        }

        CheckboxConTituloInc(incluyePrivacionRodado, { incluyePrivacionRodado = it }, "Incluir Privación de Uso de Rodado", "General: \$29.000/día  ·  Taxis y remises: \$60.000/día")
        AnimatedVisibility(visible = incluyePrivacionRodado) {
            Column(modifier = Modifier.padding(top = 8.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    RadioButton(selected = tipoRodado == "general", onClick = { tipoRodado = "general" })
                    Text("General (\$29.000/día)", modifier = Modifier.padding(start = 4.dp))
                }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    RadioButton(selected = tipoRodado == "taxi_remis", onClick = { tipoRodado = "taxi_remis" })
                    Text("Taxi / Remis (\$60.000/día)", modifier = Modifier.padding(start = 4.dp))
                }
                OutlinedTextField(
                    value = diasPrivacion,
                    onValueChange = { diasPrivacion = it.filter { c -> c.isDigit() }.take(4) },
                    label = { Text("Días de privación") },
                    suffix = { Text("días") },
                    modifier = Modifier.fillMaxWidth().resaltadoGuia(diasPrivacion.isEmpty()),
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                )
            }
        }

        // ── Nota informativa ──────────────────────────────────────────────────
        Card(colors = CardDefaults.cardColors(containerColor = colorScheme.secondaryContainer), modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.padding(12.dp)) {
                Text("ℹ️  Tabla art. 1746 CCC vigente desde el 01/08/2026", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                Text("Ingreso mensual de referencia: ${formatoPesos(INGRESO_REF)}\nLa tabla cubre edades de 18 a 65 años.", style = MaterialTheme.typography.bodySmall)
            }
        }

        if (errorMsg.isNotBlank()) {
            Text(errorMsg, color = colorScheme.error, style = MaterialTheme.typography.bodySmall)
        }

        // ── Botones ───────────────────────────────────────────────────────────
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedButton(onClick = { limpiar() }, modifier = Modifier.weight(0.4f).height(52.dp), shape = RoundedCornerShape(12.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = colorScheme.error)) {
                Icon(Icons.Default.DeleteSweep, null, Modifier.size(18.dp))
                Spacer(Modifier.width(4.dp))
                Text("Limpiar")
            }
            CalculatorActionLock(
                navController = navController,
                onAction = {
                    errorMsg = ""; resultado = null; resultadoVisibleConDelay = false
                    val edadInt = edad.toIntOrNull()
                    val pct     = porcentajeIncapacidad.toDoubleOrNull()
                    val ingreso = if (usarIngresoReferencia) INGRESO_REF else ingresoMensual.toDoubleOrNull()
                    when {
                        edadInt == null || edadInt < 0       -> errorMsg = "Ingresá una edad válida."
                        pct == null || pct <= 0 || pct > 100 -> errorMsg = "El porcentaje debe ser entre 0.01 y 100."
                        ingreso == null || ingreso <= 0      -> errorMsg = "Ingresá un ingreso mensual válido."
                        else -> {
                            resultado = calcularIndemnizacion(edadInt, pct, ingreso, tipoIncapacidad, incluyeDanoMoral, porcentajeDanoMoral.toDoubleOrNull() ?: 0.0, incluyeLucroSesante, mesesLucroSesante.toIntOrNull() ?: 0, incluyeDanoExtrapatrimonial, tipoExtrapatrimonial, incluyePrivacionRodado, diasPrivacion.toIntOrNull() ?: 0, tipoRodado)
                        }
                    }
                }
            ) { onClick ->
                Button(
                    onClick = onClick,
                    modifier = Modifier.weight(0.6f).height(52.dp),
                    shape    = RoundedCornerShape(12.dp)
                ) {
                    Icon(Icons.Default.Calculate, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Calcular", fontWeight = FontWeight.Bold)
                }
            }
        }

        // --- EFECTO DE RESULTADO CON DELAY ---
        LaunchedEffect(resultado) {
            if (resultado != null) {
                delay(400)
                resultadoVisibleConDelay = true
                delay(3000)
                scrollState.animateScrollTo(scrollState.maxValue)
            }
        }

        AnimatedVisibility(visible = resultadoVisibleConDelay) {
            resultado?.let { PanelResultadoIncapacidad(it) }
        }

        Spacer(modifier = Modifier.height(40.dp))
    }
}
}

// ─────────────────────────────────────────────────────────────────────────────
// LÓGICA DE CÁLCULO — pura Kotlin, sin dependencias Android
// ─────────────────────────────────────────────────────────────────────────────

private fun calcularIndemnizacion(
    edad: Int, porcentajeIncapacidad: Double, ingresoMensual: Double,
    tipoIncapacidad: String, incluyeDanoMoral: Boolean, porcentajeDanoMoral: Double,
    incluyeLucroSesante: Boolean, mesesLucroSesante: Int,
    incluyeDanoExtrapatrimonial: Boolean, tipoExtrapatrimonial: String,
    incluyePrivacionRodado: Boolean, diasPrivacion: Int, tipoRodado: String
): ResultadoIncapacidad {
    val factorTabla = TABLA_1746[edad]
    val detalleEdad: String
    val incapacidadBase: Double

    if (factorTabla != null) {
        incapacidadBase = (ingresoMensual / INGRESO_REF) * factorTabla * (porcentajeIncapacidad / 100.0)
        detalleEdad     = "Calculado con tabla art. 1746 CCC (edad: $edad años, factor tabla: ${formatoPesos(factorTabla)})"
    } else {
        val añosRestantes = if (edad < 18) (65 - edad) else maxOf(edad - 65, 1)
        incapacidadBase   = ingresoMensual * 12.0 * añosRestantes * (porcentajeIncapacidad / 100.0)
        detalleEdad       = "Edad fuera del rango 18–65. Cálculo orientativo según condiciones personales."
    }

    val incapacidadAjustada      = if (tipoIncapacidad.contains("Transitoria")) incapacidadBase * 0.15 else incapacidadBase
    val danoMoral                = if (incluyeDanoMoral && porcentajeDanoMoral > 0) incapacidadAjustada * (porcentajeDanoMoral / 100.0) else 0.0
    val lucroSesante             = if (incluyeLucroSesante && mesesLucroSesante > 0) ingresoMensual * mesesLucroSesante else 0.0
    val danoExtrapatrimonial     = if (incluyeDanoExtrapatrimonial) when (tipoExtrapatrimonial) {
        "muerte de hijo" -> 32_000_000.0
        "muerte de progenitor" -> 27_000_000.0
        "muerte de cónyuge/unión convivencial" -> 29_000_000.0
        else -> 8_000_000.0
    } else 0.0
    val privacionRodado          = if (incluyePrivacionRodado && diasPrivacion > 0) (if (tipoRodado == "taxi_remis") 60_000.0 else 29_000.0) * diasPrivacion else 0.0

    return ResultadoIncapacidad(
        edadVictima = edad, tipoIncapacidad = tipoIncapacidad, porcentaje = porcentajeIncapacidad,
        ingresoUsado = ingresoMensual, factorTabla = factorTabla, incapacidadBase = incapacidadAjustada,
        danoMoral = danoMoral, lucroSesante = lucroSesante, danoExtrapatrimonial = danoExtrapatrimonial,
        privacionRodado = privacionRodado,
        totalGeneral = incapacidadAjustada + danoMoral + lucroSesante + danoExtrapatrimonial + privacionRodado,
        detalleEdad = detalleEdad
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// PANEL DE RESULTADO
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun PanelResultadoIncapacidad(r: ResultadoIncapacidad) {
    val textoCopia = """
        RESUMEN INDEMNIZACIÓN POR INCAPACIDAD
        Víctima: ${r.edadVictima} años
        Tipo: ${r.tipoIncapacidad} (${r.porcentaje}%)
        Ingreso Base: ${formatoPesos(r.ingresoUsado)}
        -------------------------------------------
        RUBRO PRINCIPAL (Incapacidad): ${formatoPesos(r.incapacidadBase)}
        ${if (r.danoMoral > 0) "Daño Moral: ${formatoPesos(r.danoMoral)}" else ""}
        ${if (r.lucroSesante > 0) "Lucro Cesante: ${formatoPesos(r.lucroSesante)}" else ""}
        ${if (r.danoExtrapatrimonial > 0) "Daño Extrapatrimonial: ${formatoPesos(r.danoExtrapatrimonial)}" else ""}
        ${if (r.privacionRodado > 0) "Privación de Uso: ${formatoPesos(r.privacionRodado)}" else ""}
        -------------------------------------------
        TOTAL GENERAL: ${formatoPesos(r.totalGeneral)}
        -------------------------------------------
        Calculado con www.juridicorosario.com
    """.trimIndent()

    CajaResultadoAccionable(
        titulo = "Resultado del Cálculo",
        textoParaCompartir = textoCopia,
        colorFondo = colorScheme.surfaceVariant
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            FilaDetalleInc("Edad de la víctima", "${r.edadVictima} años")
            FilaDetalleInc("Tipo de incapacidad", r.tipoIncapacidad)
            FilaDetalleInc("Porcentaje de incapacidad", "${r.porcentaje}%")
            FilaDetalleInc("Ingreso mensual utilizado", formatoPesos(r.ingresoUsado))
            r.factorTabla?.let { FilaDetalleInc("Factor tabla (100% / ing. ref.)", formatoPesos(it)) }
            HorizontalDivider()
            FilaDetalleInc("Incapacidad (rubro principal)", formatoPesos(r.incapacidadBase), destacado = true)
            if (r.danoMoral > 0) FilaDetalleInc("Daño Moral", formatoPesos(r.danoMoral))
            if (r.lucroSesante > 0) FilaDetalleInc("Lucro Cesante / Inc. Transitoria", formatoPesos(r.lucroSesante))
            if (r.danoExtrapatrimonial > 0) FilaDetalleInc("Daño Extrapatrimonial (tope orientativo)", formatoPesos(r.danoExtrapatrimonial))
            if (r.privacionRodado > 0) FilaDetalleInc("Privación de Uso de Rodado", formatoPesos(r.privacionRodado))
            HorizontalDivider(thickness = 2.dp)
            Row(
                modifier = Modifier.fillMaxWidth().background(colorScheme.primaryContainer, RoundedCornerShape(8.dp)).padding(12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("TOTAL GENERAL", fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = colorScheme.onPrimaryContainer)
                Text(formatoPesos(r.totalGeneral), fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = colorScheme.primary)
            }
            Card(colors = CardDefaults.cardColors(containerColor = colorScheme.tertiaryContainer), modifier = Modifier.fillMaxWidth()) {
                Text(r.detalleEdad, modifier = Modifier.padding(10.dp), style = MaterialTheme.typography.bodySmall)
            }
            Text("⚠️ Cálculo orientativo conforme tabla art. 1746 CCC vigente. Los montos definitivos los determina el juez.", style = MaterialTheme.typography.bodySmall, color = colorScheme.onSurfaceVariant, textAlign = TextAlign.Justify)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTES AUXILIARES
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun SeccionTituloInc(texto: String) {
    Text(texto, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = colorScheme.primary, modifier = Modifier.padding(top = 8.dp))
    HorizontalDivider(color = colorScheme.primary.copy(alpha = 0.3f))
}

@Composable
private fun CheckboxConTituloInc(checked: Boolean, onCheckedChange: (Boolean) -> Unit, titulo: String, descripcion: String) {
    Row(verticalAlignment = Alignment.Top, modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
        Checkbox(checked = checked, onCheckedChange = onCheckedChange)
        Column(modifier = Modifier.padding(start = 8.dp)) {
            Text(titulo, fontWeight = FontWeight.Medium)
            Text(descripcion, style = MaterialTheme.typography.bodySmall, color = colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun FilaDetalleInc(label: String, valor: String, destacado: Boolean = false) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
        Text(label, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f), color = if (destacado) colorScheme.onSurface else colorScheme.onSurfaceVariant)
        Text(valor, fontWeight = if (destacado) FontWeight.Bold else FontWeight.Normal, style = MaterialTheme.typography.bodyMedium, color = if (destacado) colorScheme.primary else colorScheme.onSurface)
    }
}